import * as z from 'zod';

import { SESSION_ID_KEY } from '@/constants';

import { SESSION_TIMEOUT_MS } from './thresholds';
import { uuid } from './uuid';

/**
 * Canvasflow's own session: an id that stays the same while the reader
 * keeps doing things and is replaced after 30 minutes with no recorded
 * event. Counted from our ids, not a vendor's, so a report is the same
 * whichever backend stored the events.
 *
 * Kept in `localStorage`, so every tab of the app shares one session and a
 * reload or a deploy does not start a new one. Where storage is unavailable
 * (private mode) the session lives in memory, for that page only.
 */
const storedSchema = z.object({ id: z.uuid(), lastEventAt: z.number() });
type Session = z.infer<typeof storedSchema>;

let memory: Session | undefined;

function read(): Session | undefined {
	try {
		const stored = storedSchema.safeParse(
			JSON.parse(localStorage.getItem(SESSION_ID_KEY) ?? 'null'),
		);
		return stored.success ? stored.data : memory;
	} catch {
		return memory;
	}
}

function write(session: Session): void {
	memory = session;
	try {
		localStorage.setItem(SESSION_ID_KEY, JSON.stringify(session));
	} catch {
		// Private mode etc.: `memory` carries it for this page.
	}
}

/**
 * The session an event recorded at `now` belongs to. Calling it is what
 * keeps the session alive, so call it once per recorded event, not to peek.
 */
export function sessionIdFor(now: number): string {
	const current = read();
	const expired =
		!current ||
		now - current.lastEventAt >= SESSION_TIMEOUT_MS ||
		// A clock set backwards: do not let one stale stamp pin a session open.
		now < current.lastEventAt - SESSION_TIMEOUT_MS;
	const session = expired
		? { id: uuid(), lastEventAt: now }
		: { id: current.id, lastEventAt: Math.max(now, current.lastEventAt) };
	write(session);
	return session.id;
}

/** Test-only: forget the session held in memory. */
export function resetSessionForTests(): void {
	memory = undefined;
}
